/**
 * Notification Target Resolution
 * Uses reference fields directly from notification data — NO context RPCs.
 * Backfill (Phase 3) guarantees all notifications have reference fields populated.
 */

import { normalizeError } from '@/lib/error-types';
import type { NotificationRouteResult } from './notification-types';

/** Target type for notification navigation - no URL strings, only structured data */
export interface NotificationTarget {
  type: 'post' | 'photo' | 'profile' | 'circle';
  username?: string;
  postId?: string;
  photoId?: string;
  commentId?: string;
  isReply?: boolean;
  parentCommentId?: string;
  route?: string;
}

/**
 * Resolves notification route result to a structured target.
 * All routing data comes from reference fields on the notification row.
 * No RPC calls are made — this is a pure data transformation.
 */
export function resolveNotificationTarget(
  routeResult: NotificationRouteResult
): NotificationTarget {
  // Direct string routes - profile, connection-requests
  if (typeof routeResult === 'string') {
    if (routeResult.startsWith('/profile/')) {
      const username = routeResult.replace('/profile/', '');
      return { type: 'profile', username, route: routeResult };
    }
    if (routeResult.includes('/profile?tab=circle')) {
      return { type: 'circle', route: routeResult };
    }
    return { type: 'profile', route: routeResult };
  }

  switch (routeResult.type) {
    case 'post_like': {
      if (!routeResult.referenceUsername) {
        throw normalizeError(new Error('Missing referenceUsername for post_like notification'));
      }
      return {
        type: 'post',
        username: routeResult.referenceUsername,
        postId: routeResult.postId,
      };
    }

    case 'photo_like': {
      if (!routeResult.referenceUsername || !routeResult.referencePostId) {
        throw normalizeError(new Error('Missing reference fields for photo_like notification'));
      }
      return {
        type: 'photo',
        username: routeResult.referenceUsername,
        postId: routeResult.referencePostId,
        photoId: routeResult.photoId,
      };
    }

    case 'comment': {
      if (!routeResult.referenceUsername) {
        throw normalizeError(new Error('Missing referenceUsername for comment notification'));
      }
      // Photo-level comment
      if (routeResult.referencePhotoId) {
        return {
          type: 'photo',
          username: routeResult.referenceUsername,
          postId: routeResult.referencePostId,
          photoId: routeResult.referencePhotoId,
          commentId: routeResult.commentId,
          isReply: false,
        };
      }
      // Post-level comment
      return {
        type: 'post',
        username: routeResult.referenceUsername,
        postId: routeResult.referencePostId,
        commentId: routeResult.commentId,
        isReply: false,
      };
    }

    case 'reply': {
      if (!routeResult.referenceUsername) {
        throw normalizeError(new Error('Missing referenceUsername for reply notification'));
      }
      // Photo-level reply
      if (routeResult.referencePhotoId) {
        return {
          type: 'photo',
          username: routeResult.referenceUsername,
          postId: routeResult.referencePostId,
          photoId: routeResult.referencePhotoId,
          commentId: routeResult.commentId,
          isReply: true,
          parentCommentId: routeResult.parentCommentId,
        };
      }
      // Post-level reply
      return {
        type: 'post',
        username: routeResult.referenceUsername,
        postId: routeResult.referencePostId,
        commentId: routeResult.commentId,
        isReply: true,
        parentCommentId: routeResult.parentCommentId,
      };
    }

    case 'comment_like': {
      if (!routeResult.referenceUsername) {
        throw normalizeError(new Error('Missing referenceUsername for comment_like notification'));
      }
      // Photo-level comment like
      if (routeResult.referencePhotoId) {
        return {
          type: 'photo',
          username: routeResult.referenceUsername,
          postId: routeResult.referencePostId,
          photoId: routeResult.referencePhotoId,
          commentId: routeResult.commentId,
          isReply: !!routeResult.referenceParentCommentId,
          parentCommentId: routeResult.referenceParentCommentId,
        };
      }
      // Post-level comment like
      return {
        type: 'post',
        username: routeResult.referenceUsername,
        postId: routeResult.referencePostId,
        commentId: routeResult.commentId,
        isReply: !!routeResult.referenceParentCommentId,
        parentCommentId: routeResult.referenceParentCommentId,
      };
    }

    case 'mention': {
      if (!routeResult.referenceUsername) {
        throw normalizeError(new Error('Missing referenceUsername for mention notification'));
      }
      // Mention on a photo comment/reply
      if (routeResult.referencePhotoId) {
        return {
          type: 'photo',
          username: routeResult.referenceUsername,
          postId: routeResult.referencePostId,
          photoId: routeResult.referencePhotoId,
          commentId: routeResult.entityId,
          isReply: routeResult.entityType === 'reply',
          parentCommentId: routeResult.referenceParentCommentId,
        };
      }
      // Mention on a post comment/reply or post itself
      return {
        type: 'post',
        username: routeResult.referenceUsername,
        postId: routeResult.referencePostId ?? routeResult.entityId,
        commentId: routeResult.entityType !== 'post' ? routeResult.entityId : undefined,
        isReply: routeResult.entityType === 'reply',
        parentCommentId: routeResult.referenceParentCommentId,
      };
    }

    default:
      throw normalizeError(new Error('Unrecognized notification route type'));
  }
}
